'use client';

import clsx from 'clsx';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import styles from './index.module.scss';

// Threshold in pixels after which the header hiding behavior is activated.

const HIDDEN_THRESHOLD = 50;

/**
 * Type definition of the Header components properties.
 */

export type HeaderProps = {
  children?: ReactNode;
  isSticky?: boolean;
  isTransparent?: boolean;
  displayProgressBar?: boolean;
  className?: string;
};

/**
 * Header component.
 *
 * Handles the sticky and scroll behavior.
 *
 * Designed using the donut pattern so its children can be server components.
 */

export const Header = ({
  children,
  isSticky = true,
  isTransparent = false,
  displayProgressBar = false,
  className,
}: HeaderProps) => {
  const lastScrollYRef = useRef(0);
  const [isHidden, setIsHidden] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const lastScrollY = lastScrollYRef.current;
      const currentScrollY = window.scrollY;

      setIsScrolling((prev) => {
        const newValue = currentScrollY > HIDDEN_THRESHOLD;
        return prev !== newValue ? newValue : prev;
      });

      if (isSticky) {
        const menuOpen =
          document.querySelector('.activeDesktopMenu') ||
          document.querySelector('.activeMobileMenu');

        setIsHidden((prev) => {
          let newValue = prev;

          if (menuOpen) {
            newValue = false;
          } else if (currentScrollY < HIDDEN_THRESHOLD) {
            newValue = false;
          } else if (
            currentScrollY > HIDDEN_THRESHOLD &&
            Math.abs(currentScrollY - lastScrollY) > 4
          ) {
            newValue = currentScrollY > lastScrollY;
          }

          return prev !== newValue ? newValue : prev;
        });
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSticky]);

  const classes = clsx(
    'header',
    styles.header,
    isHidden && styles.hidden,
    isSticky && styles.sticky,
    isTransparent && [styles.transparent, 'transparent'],
    displayProgressBar && styles.progressBar,
    isScrolling && 'scrolling',
    className
  );

  return <header className={classes}>{children}</header>;
};

export default Header;
